import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  Users, Plus, Pencil, Trash2, CheckCircle2, AlertCircle, Loader2, Search,
} from "lucide-react";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";

export default function EmployeesPage() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [editing, setEditing] = useState(null); // null=closed, {}=new, {id,name}=edit
  const [submitting, setSubmitting] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const [ccOwner, setCcOwner] = useState(false);
  const [savingCc, setSavingCc] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [emps, prefs] = await Promise.all([
        api.get("/employees"),
        api.get("/coaching/preferences").catch(() => ({ data: { cc_owner: false } })),
      ]);
      setItems(emps.data?.employees || []);
      setCcOwner(!!prefs.data?.cc_owner);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "No se pudo cargar el personal");
    } finally {
      setLoading(false);
    }
  };

  const toggleCcOwner = async (next) => {
    setSavingCc(true);
    try {
      await api.put("/coaching/preferences", { cc_owner: next });
      setCcOwner(next);
      toast.success(next
        ? "Recibirás una copia de cada coaching enviado a tus empleados"
        : "No recibirás copia de los coaching a empleados");
    } catch (e) {
      toast.error("No se pudo guardar la preferencia");
    } finally {
      setSavingCc(false);
    }
  };

  useEffect(() => { load(); }, []);

  const save = async (e) => {
    e.preventDefault();
    if (!editing?.name?.trim()) return toast.error("Indica un nombre");
    setSubmitting(true);
    try {
      if (editing.id) {
        await api.patch(`/employees/${editing.id}`, { name: editing.name, email: editing.email || "", color: editing.color ?? "", pending: false });
        toast.success("Empleado actualizado");
      } else {
        await api.post("/employees", { name: editing.name, email: editing.email || "", color: editing.color ?? "" });
        toast.success("Empleado añadido");
      }
      setEditing(null);
      await load();
    } catch (e2) {
      toast.error(e2?.response?.data?.detail || "Error al guardar");
    } finally {
      setSubmitting(false);
    }
  };

  const confirmPending = async (emp) => {
    setBusyId(emp.id);
    try {
      await api.patch(`/employees/${emp.id}`, { pending: false });
      toast.success(`${emp.name} confirmado`);
      await load();
    } catch (e) {
      toast.error("Error");
    } finally {
      setBusyId(null);
    }
  };

  const remove = async (emp) => {
    if (!confirm(`¿Eliminar a "${emp.name}"? No se borran datos históricos.`)) return;
    setBusyId(emp.id);
    try {
      await api.delete(`/employees/${emp.id}`);
      toast.success("Eliminado");
      await load();
    } catch (e) {
      toast.error("Error al eliminar");
    } finally {
      setBusyId(null);
    }
  };

  const filtered = items.filter((e) => !query.trim() || e.name.toLowerCase().includes(query.toLowerCase()));
  const pendingCount = items.filter((e) => e.pending).length;

  return (
    <div className="p-8 max-w-5xl mx-auto" data-testid="employees-page">
      {/* Hero banner */}
      <div className="rounded-2xl overflow-hidden bg-slate-950 shadow-xl border border-slate-200 mb-8" data-testid="employees-hero-image">
        <img
          src="/img/farmaclass-personal.jpg"
          alt="FarmaClass · Archiduque Consulting · Tu salud, nuestra vocación"
          className="w-full h-[230px] md:h-[280px] object-cover object-center block"
          loading="lazy"
        />
      </div>
      <header className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <div className="text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold">Personal</div>
          <h1 className="font-display text-4xl font-extrabold tracking-tight">Empleados de la farmacia</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Cuando subas un Excel con desglose por empleado, los nombres detectados aparecerán aquí pendientes de revisar.
          </p>
        </div>
        <button
          onClick={() => setEditing({ name: "" })}
          className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800"
          data-testid="emp-new"
        >
          <Plus className="h-4 w-4" /> Añadir empleado
        </button>
      </header>

      {pendingCount > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 flex items-start gap-3 mb-5" data-testid="emp-pending-alert">
          <AlertCircle className="h-5 w-5 text-amber-600 mt-0.5 shrink-0" />
          <div className="text-sm text-amber-900">
            <strong>{pendingCount}</strong> empleado{pendingCount !== 1 ? "s" : ""} pendiente{pendingCount !== 1 ? "s" : ""} de confirmar.
            Se crearon automáticamente al subir un estudio. Revisa los nombres por si hay errores ortográficos y confírmalos.
          </div>
        </div>
      )}

      {/* Iter-65: Preferencia de copia al titular en los emails de coaching */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 flex items-start gap-3 mb-5" data-testid="coaching-cc-owner-block">
        <input
          id="cc-owner-toggle"
          type="checkbox"
          checked={ccOwner}
          disabled={savingCc}
          onChange={(e) => toggleCcOwner(e.target.checked)}
          className="mt-1 h-4 w-4 rounded border-slate-300 text-violet-600 focus:ring-violet-500 cursor-pointer disabled:opacity-50"
          data-testid="cc-owner-toggle"
        />
        <label htmlFor="cc-owner-toggle" className="cursor-pointer flex-1">
          <div className="text-sm font-semibold text-slate-900">
            Recibir también yo (titular) los informes de coaching que se envíen a los empleados
          </div>
          <div className="text-[12px] text-slate-500 mt-0.5">
            Cada vez que envíes un email de <strong>Coaching IA</strong> a un empleado, se te enviará una copia
            informativa a tu email de acceso con el mismo contenido (asunto prefijado con <span className="font-mono text-slate-700">[Copia titular]</span>).
            Útil para saber qué recomendaciones está recibiendo tu equipo.
          </div>
        </label>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        <div className="p-4 border-b border-slate-200 flex items-center gap-3">
          <Search className="h-4 w-4 text-slate-400" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Buscar por nombre..."
            className="flex-1 text-sm outline-none"
            data-testid="emp-search"
          />
          <div className="text-xs text-slate-500">{filtered.length} de {items.length}</div>
        </div>

        {loading ? (
          <div className="p-8 text-center text-slate-500 flex items-center justify-center gap-2">
            <Loader2 className="h-4 w-4 animate-spin" /> Cargando…
          </div>
        ) : filtered.length === 0 ? (
          <div className="p-12 text-center" data-testid="emp-empty">
            <Users className="h-8 w-8 mx-auto text-slate-300 mb-3" />
            <div className="font-display text-xl font-bold mb-1">
              {items.length === 0 ? "Sin empleados todavía" : "Sin resultados"}
            </div>
            <div className="text-slate-500 text-sm">
              {items.length === 0 ? "Pulsa \"Añadir empleado\" o sube un Excel con la columna de nombres en cabecera." : "Prueba con otro término."}
            </div>
          </div>
        ) : (
          <table className="w-full text-sm" data-testid="emp-table">
            <thead className="bg-slate-50">
              <tr>
                <th className="text-left p-4 text-[10px] uppercase tracking-wider font-bold text-slate-500">Nombre</th>
                <th className="text-left p-4 text-[10px] uppercase tracking-wider font-bold text-slate-500">Email <span className="text-slate-400 normal-case font-normal">(para coaching IA)</span></th>
                <th className="text-left p-4 text-[10px] uppercase tracking-wider font-bold text-slate-500">Estado</th>
                <th className="text-right p-4"></th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((e) => (
                <tr key={e.id} className="border-t border-slate-100" data-testid={`emp-${e.id}`}>
                  <td className="p-4 font-medium">
                    <span className="inline-flex items-center gap-2">
                      <span
                        className="h-3 w-3 rounded-full border border-slate-300 shrink-0"
                        style={{ background: e.color || "repeating-conic-gradient(#e2e8f0 0 25%, #ffffff 0 50%) 50% 50% / 6px 6px" }}
                        title={e.color ? `Color asignado: ${e.color}` : "Sin color asignado (paleta automática en las gráficas)"}
                        data-testid={`emp-color-dot-${e.id}`}
                      />
                      {e.name}
                    </span>
                  </td>
                  <td className="p-4 text-slate-600 text-xs">
                    {e.email ? (
                      <span className="font-mono-num">{e.email}</span>
                    ) : (
                      <button
                        onClick={() => setEditing({ id: e.id, name: e.name, email: "" })}
                        className="text-[10px] uppercase tracking-wider font-bold text-amber-700 bg-amber-50 hover:bg-amber-100 px-2 py-1 rounded-full"
                        data-testid={`emp-add-email-${e.id}`}
                      >
                        + Añadir email
                      </button>
                    )}
                  </td>
                  <td className="p-4">
                    {e.pending ? (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-amber-100 text-amber-700 px-2 py-1 rounded-full">
                        <AlertCircle className="h-3 w-3" /> Pendiente revisar
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 text-[10px] uppercase tracking-wider font-bold bg-emerald-100 text-emerald-700 px-2 py-1 rounded-full">
                        <CheckCircle2 className="h-3 w-3" /> Confirmado
                      </span>
                    )}
                  </td>
                  <td className="p-4 text-right">
                    <div className="inline-flex items-center gap-1">
                      {e.pending && (
                        <button
                          onClick={() => confirmPending(e)}
                          disabled={busyId === e.id}
                          className="text-[10px] uppercase tracking-wider font-bold bg-emerald-500 hover:bg-emerald-600 text-white px-3 py-1 rounded-full"
                          data-testid={`emp-confirm-${e.id}`}
                        >
                          Confirmar
                        </button>
                      )}
                      <button
                        onClick={() => setEditing({ id: e.id, name: e.name, email: e.email || "", color: e.color || "" })}
                        className="p-1.5 rounded-md text-slate-500 hover:bg-slate-100"
                        data-testid={`emp-edit-${e.id}`}
                      >
                        <Pencil className="h-4 w-4" />
                      </button>
                      <button
                        onClick={() => remove(e)}
                        disabled={busyId === e.id}
                        className="p-1.5 rounded-md text-rose-500 hover:bg-rose-50"
                        data-testid={`emp-remove-${e.id}`}
                      >
                        {busyId === e.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Trash2 className="h-4 w-4" />}
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <Dialog open={editing !== null} onOpenChange={(o) => { if (!o) setEditing(null); }}>
        <DialogContent className="sm:max-w-md">
          <form onSubmit={save}>
            <DialogHeader>
              <DialogTitle className="font-display text-2xl">
                {editing?.id ? "Editar empleado" : "Nuevo empleado"}
              </DialogTitle>
              <DialogDescription>
                Sólo necesitas su nombre. Los datos de dispensación se cargan luego en cada estudio.
              </DialogDescription>
            </DialogHeader>
            <div className="mt-4 space-y-4">
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Nombre*</span>
                <input
                  type="text"
                  value={editing?.name || ""}
                  onChange={(e) => setEditing({ ...editing, name: e.target.value })}
                  className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
                  data-testid="emp-name-input"
                  autoFocus
                  required
                />
              </label>
              <label className="block">
                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Email del empleado</span>
                <input
                  type="email"
                  value={editing?.email || ""}
                  onChange={(e) => setEditing({ ...editing, email: e.target.value })}
                  placeholder="empleado@ejemplo.com"
                  className="mt-1 w-full border border-slate-300 rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-sky-200"
                  data-testid="emp-email-input"
                />
                <span className="text-[11px] text-slate-500 mt-1 block">Si lo dejas vacío no podrás enviarle el email de Coaching IA.</span>
              </label>
              <div>
                <span className="text-[10px] uppercase tracking-[0.18em] text-slate-500 font-bold">Color en gráficas</span>
                <div className="mt-2 flex flex-wrap items-center gap-2" data-testid="emp-color-swatches">
                  {["", "#0EA5E9", "#10B981", "#F59E0B", "#E11D48", "#8B5CF6", "#14B8A6", "#F97316", "#EC4899", "#6366F1", "#EAB308", "#059669"].map((c) => {
                    const isEmpty = c === "";
                    const active = (editing?.color || "").toUpperCase() === c.toUpperCase();
                    return (
                      <button
                        key={c || "none"}
                        type="button"
                        onClick={() => setEditing({ ...editing, color: c })}
                        data-testid={`emp-color-swatch-${c ? c.replace("#", "") : "auto"}`}
                        title={isEmpty ? "Automático (paleta estándar)" : c}
                        className={`h-7 w-7 rounded-full border transition-all ${active ? "ring-2 ring-offset-2 ring-slate-950 border-slate-950" : "border-slate-300 hover:scale-110"}`}
                        style={{
                          background: isEmpty
                            ? "repeating-conic-gradient(#e2e8f0 0 25%, #ffffff 0 50%) 50% 50% / 8px 8px"
                            : c,
                        }}
                      />
                    );
                  })}
                  <div className="inline-flex items-center gap-1 ml-1">
                    <input
                      type="color"
                      value={editing?.color || "#0ea5e9"}
                      onChange={(e) => setEditing({ ...editing, color: e.target.value.toUpperCase() })}
                      data-testid="emp-color-picker"
                      className="h-7 w-7 rounded cursor-pointer border border-slate-300 p-0 bg-white"
                      title="Elige un color personalizado"
                    />
                    {editing?.color && (
                      <span className="text-[10px] font-mono-num text-slate-500 uppercase">{editing.color}</span>
                    )}
                  </div>
                </div>
                <span className="text-[11px] text-slate-500 mt-1 block">
                  Se usa para identificar a este empleado en las gráficas de evolución. Deja "automático" (cuadro gris) para que FarmaClass asigne uno de la paleta estándar.
                </span>
              </div>
            </div>
            <DialogFooter className="!justify-between gap-2 mt-4">
              <button type="button" onClick={() => setEditing(null)} className="px-4 py-2 rounded-md text-sm text-slate-600 hover:bg-slate-100" data-testid="emp-cancel">
                Cancelar
              </button>
              <button
                type="submit"
                disabled={submitting}
                className="inline-flex items-center gap-2 bg-sky-500 hover:bg-sky-600 disabled:opacity-60 text-white font-bold px-5 py-2.5 rounded-full"
                data-testid="emp-save"
              >
                {submitting && <Loader2 className="h-4 w-4 animate-spin" />}
                Guardar
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
